<script setup lang="ts">
/**
 * 🔴 **THE BOTTOM RAIL** — the thin strip across the foot of every signed-in screen (D28, D42).
 *
 * Metaship's, brought in on Devin's instruction: terms · the release stamp · a NEW VERSION pill when
 * one is waiting · the theme toggle · help. Five things, and what they have in common is that they
 * are true of the WHOLE APPLICATION rather than of whatever is on screen — which is exactly why they
 * belong on a rail and not in a module.
 *
 * It is 26 pixels tall and it stays out of the way. A person should be able to work all day without
 * reading it, and find every one of these five things without being taught where they are.
 *
 * 🔴 **The pill is the reason the rail is worth its pixels.** A production desk keeps a tab open for
 * weeks; without somewhere to say so, a fix shipped on Monday reaches that person when they next
 * restart their browser, which may be never. See `pwa/newVersion.ts`.
 */
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { applyNewVersion, newVersionReady } from "@/pwa/newVersion";
import { RELEASE_LABEL } from "./release";
import { useThemeToggle } from "./useThemeToggle";
import { APP_LOGIN_PAGE } from "@/app";

/**
 * 🔴 **On a phone the rail is not a strip; it is a row in the drawer** (Devin, 2026-09-25: *"we can drop
 * this bar in the mobile view, it is wasting screen real estate"*). 26 pixels of every phone screen for
 * five things nobody reads while working is the wrong trade. `inDrawer` draws the same things inline at
 * the foot of the nav drawer, minus the theme toggle the drawer's account controls already carry.
 */
const props = defineProps<{ inDrawer?: boolean }>();
const { t } = useI18n();
const { darkMode, toggleTheme } = useThemeToggle();

/**
 * The tenant's own terms where it has them (`tenant/types.ts`). Absent, the link is not drawn: an
 * app that has published none has nothing to point at, and a dead link is worse than no link.
 */
const termsUrl = APP_LOGIN_PAGE.termsUrl;

/** Support, as a URL or a mailto — whichever the tenant configured. */
const helpHref = computed(() => {
  const page = APP_LOGIN_PAGE;
  if (page?.supportUrl) return page.supportUrl;
  if (page?.supportEmail) return `mailto:${page.supportEmail}`;
  return null;
});
</script>

<template>
  <footer class="sh-rail" :class="{ 'sh-rail--drawer': props.inDrawer }" :data-testid="props.inDrawer ? 'drawer-rail' : 'bottom-rail'">
    <a
      v-if="termsUrl"
      class="sh-rail__link"
      :href="termsUrl"
      target="_blank"
      rel="noopener noreferrer"
      >{{ props.inDrawer ? t("preauth.terms") : t("rail.terms") }}</a
    >

    <!-- Which build is on screen. Omitted in a dev server, where there is no build to name. -->
    <span v-if="RELEASE_LABEL" class="sh-rail__release" :title="t('rail.releaseTitle')">{{ RELEASE_LABEL }}</span>

    <!-- 🔴 Only when one is actually waiting, and it never reloads by itself: the moment to lose an
         unsaved take belongs to the person, not to the deploy. -->
    <button
      v-if="newVersionReady"
      type="button"
      class="sh-rail__pill"
      :title="t('rail.newVersionTitle')"
      data-testid="new-version"
      @click="applyNewVersion()"
    >
      <span class="sh-rail__pip" aria-hidden="true"></span>
      {{ t("rail.newVersion") }}
    </button>

    <span class="sh-rail__gap"></span>

    <button
      v-if="!props.inDrawer"
      type="button"
      class="sh-rail__icon"
      :title="darkMode ? t('theme.toLight') : t('theme.toDark')"
      :aria-label="darkMode ? t('theme.toLight') : t('theme.toDark')"
      @click="toggleTheme"
    >
      <Transition name="sh-icon-swap" mode="out-in">
        <v-icon :key="darkMode ? 'sun' : 'moon'" size="15">{{ darkMode ? "light_mode" : "dark_mode" }}</v-icon>
      </Transition>
    </button>

    <a
      v-if="helpHref"
      class="sh-rail__icon"
      :href="helpHref"
      :target="helpHref.startsWith('mailto:') ? undefined : '_blank'"
      rel="noopener noreferrer"
      :title="t('rail.help')"
      :aria-label="t('rail.help')"
    >
      <v-icon size="15">help_outline</v-icon>
    </a>
  </footer>
</template>

<style scoped>
/* Fixed to the foot of the viewport, above a module's content but never over it: modules reserve the
   strip with `--pinksy-rail-height`, the same contract they already honour for the header. */
.sh-rail {
  position: fixed;
  inset: auto 0 0 0;
  z-index: 1004; /* over a module, under the nav drawer (1005) and its scrim */
  display: flex;
  align-items: center;
  gap: 12px;
  /* The home indicator sits under the rail, never on its icons (viewport-fit=cover; App.vue's footer). */
  height: calc(var(--pinksy-rail-height, 26px) + env(safe-area-inset-bottom));
  padding: 0 12px env(safe-area-inset-bottom);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--sh-text-60);
  background: var(--sh-surface, rgb(var(--v-theme-surface)));
  border-top: 1px solid var(--sh-hairline);
}
/* In the phone drawer: an inline row at the foot of the account controls, not a fixed strip. */
.sh-rail--drawer {
  position: static;
  height: auto;
  min-height: 36px;
  padding: 6px 12px 10px;
  gap: 10px;
  background: transparent;
  border-top: 1px solid var(--sh-hairline);
}
.sh-rail--drawer .sh-rail__release {
  white-space: nowrap;
}
.sh-rail--drawer .sh-rail__link {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sh-rail--drawer .sh-rail__icon {
  width: 36px;
  height: 36px;
}
.sh-rail__gap {
  flex: 1 1 auto;
}
.sh-rail__link {
  color: inherit;
  text-decoration: none;
  transition: color 140ms ease;
}
.sh-rail__link:hover {
  color: rgb(var(--v-theme-primary));
}
/* The stamp is a fact, not a control: it never lights up, and it is tabular so a
   changing week does not shuffle the things beside it. */
.sh-rail__release {
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
  cursor: default;
}

/* ── NEW VERSION ───────────────────────────────────────────────────────────
   The one thing on this rail that asks for attention, so it is the one thing
   that gets colour. A slow pulse rather than a blink: it is not urgent, and a
   blinking element in the corner of a working screen is a reason to close the
   tab rather than to press it. */
.sh-rail__pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 17px;
  padding: 0 8px;
  border: 0;
  border-radius: 999px;
  font: inherit;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.13);
  cursor: pointer;
  transition:
    background-color 140ms ease,
    transform 140ms ease;
}
.sh-rail__pill:hover {
  background: rgba(var(--v-theme-primary), 0.2);
}
.sh-rail__pill:active {
  transform: scale(0.97);
}
.sh-rail__pip {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  animation: sh-rail-pulse 2.4s ease-in-out infinite;
}
@keyframes sh-rail-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
@media (prefers-reduced-motion: reduce) {
  .sh-rail__pip {
    animation: none;
  }
}

.sh-rail__icon {
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: none;
  color: inherit;
  cursor: pointer;
  transition:
    color 140ms ease,
    background-color 140ms ease;
}
.sh-rail__icon:hover {
  color: rgb(var(--v-theme-primary));
  background: var(--sh-hover);
}

.sh-icon-swap-enter-active,
.sh-icon-swap-leave-active {
  transition:
    opacity 140ms ease,
    transform 140ms ease;
}
.sh-icon-swap-enter-from,
.sh-icon-swap-leave-to {
  opacity: 0;
  transform: rotate(-30deg) scale(0.8);
}
/*
 * 🔴 A THUMB'S SIZE on a 26px strip (hardening, WCAG 2.5.8): on a touch screen each control's hit area
 * reaches past its drawn icon to 44px — the strip stays the published 26px — and the words read at 12px.
 */
@media (pointer: coarse) {
  .sh-rail {
    font-size: 12px;
  }
  .sh-rail__icon,
  .sh-rail__link,
  .sh-rail__pill {
    position: relative;
  }
  .sh-rail__icon::after,
  .sh-rail__link::after,
  .sh-rail__pill::after {
    content: "";
    position: absolute;
    inset: -10px -12px;
  }
}
</style>
